import { useEffect, useState } from "react";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import InputUnderline from "../../components/controls/input/InputUnderline";
import Input from "../../components/controls/input/customInput/CustomInput";
import Modal from "../../components/controls/modal/Modal";
import ModalCheckboxWarning from "../../components/controls/modalcheckbox/ModalCheckboxWarning";
import Searchable from "../../components/controls/select/CustomSelect";
import {
  getLabelByType,
  taskPriorityOptions,
  taskStatusOptions
} from "../../helpers/utils";
import scopeService from "../../store/services/scope.service";
import searchService from "../../store/services/search.service";

import { useDispatch } from "react-redux";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import ModalCheckbox from "../../components/controls/modalcheckbox/ModalCheckbox";
import DateRangeWithDuration from "../../components/dateRangeWithDuration/DateRangeWithDuration";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import TaskDependencies from "../../components/taskDependencies/TaskDependencies";
import {
  CalendarIcon
} from "../../modules/default-svg";
import { AlertTriangle, XCircle, X } from "lucide-react";
import SafeHtml from "../../components/safeHtml/SafeHtml";
import { generalActions } from "../../store/actions";
import { getProjectProgressSummary } from "../../store/actions/projects.actions";
import { getAccountCurrency, isDateGreaterThan } from "../../utils/systemSettings";
import AssociatedRisks from "../risk/AssociatedRisks";
import {
  searchScopeHandler,
  nextWorkingDayAfter,
  calendarDaysDiff,
  shiftDateStr,
} from "./utils";
import TaskResourceAllocationManager from "../../components/taskResourceAllocation/TaskResourceAllocationManager";

const CreateEditTask = (props) => {
  const
    {
      taskId,
      task,
      isEdit,
      scopeID,
      scopeName,
      lockScope = false,
      initialTaskGroup = null,
      projectID,
      projectName,
      isOpen,
      onClose,
      onSave,
      setRowData,
      plannerAllocationData,
      isViewOnly = false
    } = props;

  const dispatch = useDispatch();
  // States
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [scopes, setScopes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [project, setProject] = useState(projectID ? { label: projectName, value: projectID } : null);
  // Task States
  const [name, setName] = useState("");
  const [risks, setRisks] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [status, setStatus] = useState(0);
  const [priority, setPriority] = useState(0);
  const [phase, setPhase] = useState("");
  const [scope, setScope] = useState(scopeID ? { label: scopeName, value: scopeID } : null);
  const [taskGroup, setTaskGroup] = useState(initialTaskGroup || null);
  const [taskGroupOptions, setTaskGroupOptions] = useState([]);
  const [refId, setRefId] = useState("");
  const [description, setDescription] = useState("");
  const [isDraft, setIsDraft] = useState(false);
  const [isCritical, setIsCritical] = useState(false);
  const [isMilestone, setIsMilestone] = useState(false);
  const [isInWsr, setIsInWsr] = useState(false);
  const [taskProgress, setTaskProgress] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [dependenciesDto, setDependenciesDto] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [taskDateConflicts, setTaskDateConflicts] = useState([]);


  const [formErrors, setFormErrors] = useState({
    name: "",
    startDate: "",
    endDate: "",
    completionDate: "",
    scope: "",
  });

  const effectiveScopeId = scope?.value || scopeID;

  // Handle Reset Modal States
  const handleReset = () => {
    if (onClose) {
      onClose();
    }
    setIsDraft(false);
    setIsCritical(false);
    setIsMilestone(false);
    setIsInWsr(false);
    setTaskProgress(0);
    setDependencies([]);
    setDependenciesDto([]);
    setAllocations([]);
    setFormErrors({});
    setIsLoading(false);
    setScope(null);
    setTaskGroup(null);
    setTaskGroupOptions([]);
    setAttachments([]);
  };

  const handleScopeChange = (val) => {
    setScope(val);
    // A task group belongs to a specific scope; a different scope means the
    // previously selected group no longer applies.
    setTaskGroup(null);
    setTaskGroupOptions([]);
  };

  const searchTaskGroupHandler = (value) => {
    if (!effectiveScopeId) {
      setTaskGroupOptions([]);
      return;
    }
    searchService
      .TaskGroups(value, { scopeId: effectiveScopeId })
      .then((res) => {
        setTaskGroupOptions((res || []).map((obj) => ({ label: obj.name, value: obj.id })));
      })
      .catch(() => setTaskGroupOptions([]));
  };

  // Handle Save Operations for Create/Update Task
  const handleSave = () => {

    const scopeId = scope ? scope.value : scopeID;
    const errors = {};
    if (!name) errors["name"] = `The Name field is required.`;
    if (!scope) errors["scope"] = `The Scope field is required.`;
    if (startDate && endDate && isDateGreaterThan(startDate, endDate)) {
      errors[
        "endDate"
      ] = `End date must be equal to or greater than start date.`;
    }
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }

    // Build AssignedResources payload
    const assignedResourcesPayload = (allocations || []).map((a) => ({
      resourceId: a.resource?.id,
      hoursPerDay: a.hoursPerDay,
      startDate: a.startDate,
      endDate: a.endDate,
      isIncludeWeekends: a.isIncludeWeekends,
    }))


    const payload = {
      ...(name && { Name: name }),
      ...(refId && { RefId: refId }),
      ...(startDate && { startDate: startDate }),
      ...(endDate && { endDate: endDate }),
      ...(completionDate && { completionDate: completionDate }),
      ...(description && { Description: description }),
      ...(phase && { Phase: phase }),
      ...(isDraft && { isDraft: isDraft }),
      ...(isCritical && { isCritical: isCritical }),
      ...(isMilestone && { isMilestone: isMilestone }),
      ...(isInWsr && { isInWsr: isInWsr }),
      ScopeId: scopeId,
      TaskGroupId: taskGroup?.value || null,
      projectId: projectID,
      AssignedResources: assignedResourcesPayload,
      progress: taskProgress,
      ...({ Dependencies: dependenciesDto }),
      status,
      priority
    };



    setIsButtonLoading(true);
    if (task || taskId) {
      scopeService
        .updateTaskRequest({
          id: taskId || task.id,
          payload,
        })
        .then((response) => {
          const result = response?.data ?? response;
          const value = result?.value ?? result;

          if (value && value.isPermissible === false) {
            setTaskDateConflicts(value.conflicts || []);
            setIsButtonLoading(false);
            return;
          }

          setTaskDateConflicts([]);
          dispatch(
            generalActions.showToast({
              title: "Task updated successfully",
              type: "success",
            })
          );
          dispatch(getProjectProgressSummary({ id: project?.value }));
          handleReset();
          if (onSave) {
            onSave();
          }
        })
        .catch((error) => {
          dispatch(
            generalActions.showToast({
              title: "Something went wrong. Please try again later.",
              type: "error",
            })
          );
        })
        .finally(() => {
          if (!onSave) {
            scopeService
              .taskRequest({
                parent: "Scope",
                id: scopeID,
              })
              .then((results) => {
                setRowData(results.data);
              })
              .catch((error) => {
                dispatch(
                  generalActions.showToast({
                    title: "Something went wrong. Please try again later.",
                    type: "error",
                  })
                );
              });
          }
        });
    } else {
      scopeService
        .createTaskRequest(payload)
        .then((response) => {
          const result = response?.data ?? response;
          const value = result?.value ?? result;

          if (value && value.isPermissible === false) {
            setTaskDateConflicts(value.conflicts || []);
            setIsButtonLoading(false);
            return;
          }

          setTaskDateConflicts([]);
          dispatch(
            generalActions.showToast({
              title: "Task created successfully",
              type: "success",
            })
          );
          dispatch(getProjectProgressSummary({ id: project?.value }));
          handleReset();
          if (onSave) {
            onSave();
          }
        })
        .catch((error) => {
          dispatch(
            generalActions.showToast({
              title: "Something went wrong. Please try again later.",
              type: "error",
            })
          );
        })
        .finally(() => {
          if (!onSave) {
            scopeService
              .taskRequest({
                parent: "Scope",
                id: scopeID,
                page: 1,
                size: 20
              })
              .then((results) => {
                setRowData(results.data);
              })
              .catch((error) => {
                dispatch(
                  generalActions.showToast({
                    title: "Something went wrong. Please try again later.",
                    type: "error",
                  })
                );
              });
          }
        });
    }
  };

  const handleAllocationsChange = (allocs) => {
    setAllocations(allocs);

    // Auto-update task dates from allocation min/max
    const dates = allocs
      .filter((a) => (a.startDate || a.StartDate) && (a.endDate || a.EndDate))
      .map((a) => ({ start: a.startDate || a.StartDate, end: a.endDate || a.EndDate }));

    if (dates.length > 0) {
      const minStart = dates.reduce((min, d) => (d.start < min ? d.start : min), dates[0].start);
      const maxEnd = dates.reduce((max, d) => (d.end > max ? d.end : max), dates[0].end);
      setStartDate(minStart);
      setEndDate(maxEnd);
    }
  };

  const handleTaskDependenciesChange = (dependencies) => {
    setDependenciesDto(dependencies);

    if (!dependencies?.length) return;

    // ── Step 1: compute the most-restrictive required start date ─────────────
    let requiredStart = null;
    for (const dep of dependencies) {
      let depRequired = null;
      if (dep.dependencyType === 0 && dep.endDate) {
        // FS: this task must start the next working day after the dep ends
        depRequired = nextWorkingDayAfter(dep.endDate);
      } else if (dep.dependencyType === 1 && dep.startDate) {
        // SS: this task must start on the same day as the dep
        depRequired = dep.startDate.split('T')[0];
      }
      if (depRequired && (!requiredStart || depRequired > requiredStart)) {
        requiredStart = depRequired;
      }
    }

    if (!requiredStart) return;

    // ── Step 2: bail early if the current start already satisfies every dep ──
    if (startDate && requiredStart <= startDate) return;

    const hasProgress = Number(taskProgress) > 0;
    const deltaDays = startDate ? calendarDaysDiff(startDate, requiredStart) : 0;

    // ── Step 3: shift task dates ──────────────────────────────────────────────
    if (!hasProgress) {
      setStartDate(requiredStart);
      if (endDate && deltaDays > 0) {
        setEndDate(shiftDateStr(endDate, deltaDays));
      }
    } else {
      // Progress > 0: start date is frozen — only shift endDate
      if (endDate && deltaDays > 0) {
        setEndDate(shiftDateStr(endDate, deltaDays));
      }
    }

    // ── Step 4: shift allocations that have dates ─────────────────────────────
    if (allocations?.length > 0 && deltaDays > 0) {
      const updatedAllocations = allocations.map((a) => {
        const aStart = a.startDate;
        const aEnd = a.endDate;
        if (!aStart && !aEnd) return a;

        if (!hasProgress) {
          return {
            ...a,
            startDate: aStart ? shiftDateStr(aStart, deltaDays) : null,
            endDate: aEnd ? shiftDateStr(aEnd, deltaDays) : null,
            isDirty: true,
          };
        } else {
          if (!aEnd) return a;
          return { ...a, endDate: shiftDateStr(aEnd, deltaDays), isDirty: true };
        }
      });
      setAllocations(updatedAllocations);
    }
  };

  // Components
  const formBody = () => {
    return (
      <>
        <div className="d-flex align-items-end justify-content-between">
          <div className="col-10">
            <InputUnderline
              placeholder="Add task name"
              value={name}
              error={formErrors.name}
              handleChange={setName}
              isRequired
              loading={isLoading}
              disabled={isViewOnly}
            />
          </div>
          <div className="w-20 ml-2">
            <Input
              icon="%"
              placeholder="0"
              max={100}
              value={taskProgress}
              type="number"
              handleChange={setTaskProgress}
              label="Progress"
              loading={isLoading}
              isDisabled={isViewOnly}
            />
          </div>
        </div>
        <div className="flex col-10 gap-3 mt-3">
          <ModalCheckboxWarning
            label={isCritical ? "Critical" : "Mark as Critical"}
            checked={isCritical}
            setChecked={setIsCritical}
            disabled={isViewOnly}
            loading={isLoading}
          />
          <ModalCheckbox
            label={isMilestone ? "Milestone" : "Mark as Milestone"}
            checked={isMilestone}
            setChecked={setIsMilestone}
            disabled={isViewOnly}
            loading={isLoading}
          />
          <ModalCheckbox
            label={isInWsr ? "In WSR" : "Include in WSR"}
            checked={isInWsr}
            setChecked={setIsInWsr}
            disabled={isViewOnly}
            loading={isLoading}
          />
        </div>
        <TaskDependencies
          projectId={project?.value}
          scope={scope}
          taskDependencies={dependencies}
          onDependenciesChange={handleTaskDependenciesChange}
          disabled={isViewOnly}
          loading={isLoading}
        />

        {isEdit && <AssociatedRisks isLoading={isLoading} risks={risks} />}

        <div className="row align-items-center gx-3 my-3">
          <div className="col-6">
            {isEdit || lockScope ? (
              <div>
                <label className="Input_label">Scope</label>
                <div>
                  <span
                    className="d-inline-flex align-items-center text-sm-400"
                    style={{
                      background: "#ECE7FD",
                      color: "#342C82",
                      borderRadius: "14px",
                      padding: "4px 12px",
                    }}
                  >
                    {scope?.label || scopeName}
                  </span>
                </div>
              </div>
            ) : (
              <Searchable
                label="Scope"
                SelectText="Scope"
                searchText={(value) =>
                  searchScopeHandler(value, project?.value, scopeService, setScopes)
                }
                options={scopes}
                defaultVal={scope}
                onChange={handleScopeChange}
                loading={isLoading}
                isRequired
                errorMessage={formErrors.scope}
                isDisabled={isViewOnly}
              />
            )}
          </div>
          <div className="col-6">
            <Searchable
              label="Task Group"
              SelectText={effectiveScopeId ? "a task group (optional)" : "a scope first"}
              searchText={searchTaskGroupHandler}
              options={taskGroupOptions}
              defaultVal={taskGroup}
              onChange={setTaskGroup}
              loading={isLoading}
              isDisabled={!effectiveScopeId || isViewOnly}
            />
          </div>
        </div>

        <div className="row align-items-center gx-3 my-3">
          <TaskResourceAllocationManager
            scopeId={scope?.value}
            taskId={isEdit && (task ? task?.id : taskId)}
            allocations={allocations}
            onChange={handleAllocationsChange}
            onTaskDateConflicts={setTaskDateConflicts}
            loading={isLoading}
            buttonLabel="Assign Resources"
            disabled={isViewOnly}
            taskStartDate={startDate}
            taskEndDate={endDate}
          />
        </div>
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-12">
            {taskDateConflicts.length > 0 && (() => {
              const hasError = taskDateConflicts.some(c => c.conflictType === "ResourceCapacity");
              const borderColor = hasError ? "#f87171" : "#fb923c";
              const bgColor = hasError ? "#fef2f2" : "#fff7ed";
              const headerColor = hasError ? "#b91c1c" : "#c2410c";
              return (
                <div style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: "8px",
                  background: bgColor,
                  padding: "10px 14px",
                  marginBottom: "12px",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <span style={{ fontWeight: 600, fontSize: "14px", color: headerColor }}>Date conflicts detected</span>
                    <button
                      type="button"
                      onClick={() => setTaskDateConflicts([])}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: headerColor }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {Object.entries(
                    taskDateConflicts.reduce((acc, c) => {
                      const key = c.conflictType || "Other";
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(c);
                      return acc;
                    }, {})
                  ).map(([type, items], idx) => (
                    <div key={type} style={idx > 0 ? { marginTop: "8px", paddingTop: "8px", borderTop: "1px solid rgba(0,0,0,0.06)" } : undefined}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "4px" }}>
                        {type === "ResourceCapacity"
                          ? <XCircle size={16} style={{ color: "#dc2626", flexShrink: 0 }} />
                          : <AlertTriangle size={16} style={{ color: "#ea580c", flexShrink: 0 }} />}
                        <span>{type.replace(/([a-z])([A-Z])/g, "$1 $2")}</span>
                      </div>
                      <ul style={{ margin: 0, padding: "0 0 0 22px", listStyle: "none", display: "flex", flexDirection: "column", gap: "4px" }}>
                        {items.map((c, i) => (
                          <li key={i} style={{ fontSize: "13px", color: "#1a1a1a", lineHeight: 1.4 }}>
                            <SafeHtml html={c.message} className="" style={{ flex: 1 }} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          <DateRangeWithDuration
            isLoading={isLoading}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startDateError={formErrors.startDate}
            endDateError={formErrors.endDate}
            isStartDateDisabled={Number(taskProgress) > 0}
            disabled={isViewOnly}
            icon={<CalendarIcon />}
          />
          <div className="col-4">
            <Input
              icon={<CalendarIcon />}
              value={completionDate}
              minDate={endDate || startDate}
              type="date"
              handleChange={setCompletionDate}
              label="Completion Date"
              errorMessage={formErrors.completionDate}
              loading={isLoading}
              isDisabled={isViewOnly}
            />
          </div>
        </div>
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-3">
            <DropDown
              label="Priority"
              options={taskPriorityOptions}
              defaultVal={getLabelByType("taskPriority", priority)}
              onChange={setPriority}
              loading={isLoading}
              disabled={isViewOnly}
            />
          </div>
          <div className="col-3">
            <DropDown
              label="Status"
              options={taskStatusOptions}
              defaultVal={getLabelByType("taskStatus", status)}
              onChange={setStatus}
              loading={isLoading}
              disabled={isViewOnly}
            />
          </div>
          <div className="col-3">
            <Input
              icon={false}
              placeholder="Enter phase"
              value={phase}
              type="text"
              handleChange={setPhase}
              label="Phase"
              loading={isLoading}
              isDisabled={isViewOnly}
            />
          </div>
          <div className="col-3">
            <Input
              icon={false}
              placeholder="Enter Change Ticket"
              value={refId}
              type="text"
              handleChange={setRefId}
              label="Change Ticket"
              loading={isLoading}
              isDisabled={isViewOnly}
            />
          </div>
        </div>
        <RichTextEditor
          wrapperClass="mb-3"
          label="Description"
          content={description}
          onChange={setDescription}
          loading={isLoading}
          disabled={isViewOnly}
        />
        {
          isEdit && (task || taskId) &&
          <EntityFileUpload
            entityId={task?.id || taskId}
            entityType="Task"
            attachments={attachments}
            onAttachmentsChange={() => {
              if (task?.id || taskId) {
                scopeService
                  .getTaskAttachements({ id: task?.id || taskId })
                  .then((attachements) => {
                    setAttachments(attachements || []);
                  })
                  .catch(() => { });
              }
            }}
            disabled={isLoading || (!task?.id && !taskId) || isViewOnly}
          />
        }

      </>
    );
  };

  const setTaskDetails = (taskDetails) => {
    setName(taskDetails?.name);
    setAllocations(taskDetails?.allocations || []);
    setStartDate(taskDetails?.startDate);
    setEndDate(taskDetails?.endDate);
    setCompletionDate(
      taskDetails?.completionDate
    );
    if (taskDetails?.scope) {
      setScope({
        label: taskDetails?.scope.name,
        value: taskDetails?.scope.id,
      });
    }
    setTaskGroup(
      taskDetails?.taskGroup
        ? { label: taskDetails.taskGroup.name, value: taskDetails.taskGroup.id }
        : null
    );
    setPriority(taskDetails?.priority);
    setPhase(taskDetails?.phase);
    setTaskProgress(taskDetails?.progress);
    setStatus(taskDetails?.status);
    setRefId(taskDetails?.refId);
    setDescription(taskDetails?.description);
    setIsCritical(taskDetails?.isCritical);
    setIsMilestone(taskDetails?.isMilestone);
    setIsDraft(taskDetails?.isDraft);
    setIsInWsr(taskDetails?.isInWsr);
    setDependencies(taskDetails?.dependencies);
    setRisks(taskDetails?.risks);
    setAttachments(taskDetails?.attachments || []);
  };

  const fetchTaskData = (taskId) => {
    if (!taskId)
      return;

    setIsLoading(true);
    scopeService
      .getTaskDetails({ id: taskId })
      .then((taskData) => {
        setTaskDetails(taskData);
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      }).finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isEdit) {
      if (task && Object.keys(task).length > 0) {
        setIsLoading(true);
        setTaskDetails(task)
        setIsLoading(false);
      }
      else if (taskId) {
        fetchTaskData(taskId);
      }
    }
  }, [isEdit, task, taskId]);

  useEffect(() => {
    if (plannerAllocationData) {
      setAllocations([...plannerAllocationData]);
      setStartDate(plannerAllocationData[0].startDate);
      setEndDate(plannerAllocationData[0].endDate);
    }
  }, [plannerAllocationData]);

  // Don't render anything if modal should not be open
  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      title={`${isEdit ? (!isViewOnly ? "Edit" : "") : "Create"} Task ${isViewOnly ? "Details" : ""}`}
      handleCancel={handleReset}
      handleSave={!isViewOnly && handleSave}
      saveBtnLabel="Save"
      cancelBtnLabel="Cancel"
      isLoading={isButtonLoading}
      body={formBody()}
      size="large"
      footer={!isViewOnly}
      width={900}
    />
  );
};

export default CreateEditTask;
